import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import {
  FREQUENTLY_ASKED_QUESTIONS_ACTIONS,
  FREQUENTLY_ASKED_QUESTIONS_CATEGORY,
  gaPush,
} from '@/configuration/setup/googleAnalytics';
import { setCurrentPage } from '@/store/about/aboutSlice';
import { setCurrentLayout } from '@/store/layout/layoutSlice';
import { resetActiveThread } from '@/store/thread/threadSlice';

import PageNavigation from '../PageNavigation/PageNavigation';
import { Card, CardContainer, CardContent, CardTitle, NavPills } from './Style';

const promptText = {
  introduction: 'getStartedPage.frequentlyAskedQuestions.introduction',
  aiBasicsHeading: 'getStartedPage.frequentlyAskedQuestions.aiBasics.title',
  aiBasicsQuestionsAnswers: [
    {
      question: 'getStartedPage.frequentlyAskedQuestions.aiBasics.questionOne',
      answer: 'getStartedPage.frequentlyAskedQuestions.aiBasics.answerOne',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.aiBasics.questionTwo',
      answer: 'getStartedPage.frequentlyAskedQuestions.aiBasics.answerTwo',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.aiBasics.questionThree',
      answer: 'getStartedPage.frequentlyAskedQuestions.aiBasics.answerThree',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.aiBasics.questionFour',
      answer: 'getStartedPage.frequentlyAskedQuestions.aiBasics.answerFour',
    },
  ],
  promptingHeading: 'getStartedPage.frequentlyAskedQuestions.prompting.title',
  promptingQuestionsAnswers: [
    {
      question: 'getStartedPage.frequentlyAskedQuestions.prompting.questionOne',
      answer: 'getStartedPage.frequentlyAskedQuestions.prompting.answerOne',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.prompting.questionTwo',
      answer: 'getStartedPage.frequentlyAskedQuestions.prompting.answerTwo',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.prompting.questionThree',
      answer: 'getStartedPage.frequentlyAskedQuestions.prompting.answerThree',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.prompting.questionFour',
      answer: 'getStartedPage.frequentlyAskedQuestions.prompting.answerFour',
    },
  ],
  dataProtectionAndProcessingHeading: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.title',
  dataProtectionAndProcessingQuestionsAnswers: [
    {
      question: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.questionOne',
      answer: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.answerOne',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.questionTwo',
      answer: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.answerTwo',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.questionThree',
      answer: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.answerThree',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.questionFour',
      answer: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.answerFour',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.questionFive',
      answer: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.answerFive',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.questionSix',
      answer: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.answerSix',
    },
    {
      question: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.questionSeven',
      answer: 'getStartedPage.frequentlyAskedQuestions.dataProtectionAndProcessing.answerSeven',
    },
  ],
};

const FrequentlyAskedQuestions = () => {
  const dispatch = useDispatch();
  const pageTopRef = useRef<HTMLDivElement>(null);

  const handlePreviousSection = () => dispatch(setCurrentPage('page3'));
  const handleGoToChat = () => {
    dispatch(resetActiveThread());
    dispatch(setCurrentLayout('chat'));
    gaPush({
      category: FREQUENTLY_ASKED_QUESTIONS_CATEGORY,
      action: FREQUENTLY_ASKED_QUESTIONS_ACTIONS.CLICK_START_CHAT,
      label: 'User clicked start chat',
    });
  };

  useEffect(() => {
    if (pageTopRef.current) {
      pageTopRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const [activeTab, setActiveTab] = useState('aiBasics');

  const renderCards = (items: { question: string; answer: string }[]) => {
    return (
      <CardContainer>
        {items.map((item, index) => (
          <Card key={index}>
            <CardTitle className="text-color-primary">
              <FormattedMessage id={item.question} />
            </CardTitle>
            <CardContent>
              <FormattedMessage id={item.answer} />
            </CardContent>
          </Card>
        ))}
      </CardContainer>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'aiBasics':
        return renderCards(promptText.aiBasicsQuestionsAnswers);
      case 'prompting':
        return renderCards(promptText.promptingQuestionsAnswers);
      case 'dataProtection':
        return renderCards(promptText.dataProtectionAndProcessingQuestionsAnswers);
      default:
        return null;
    }
  };

  return (
    <div className="margin-top-25">
      <p className="margin-bottom-20">
        <FormattedMessage id={promptText.introduction} />
      </p>
      <div ref={pageTopRef} style={{ position: 'absolute', top: '0' }} />
      <NavPills>
        <li className={activeTab === 'aiBasics' ? 'active' : ''} onClick={() => setActiveTab('aiBasics')}>
          <FormattedMessage id={promptText.aiBasicsHeading} />
        </li>
        <li className={activeTab === 'prompting' ? 'active' : ''} onClick={() => setActiveTab('prompting')}>
          <FormattedMessage id={promptText.promptingHeading} />
        </li>
        <li className={activeTab === 'dataProtection' ? 'active' : ''} onClick={() => setActiveTab('dataProtection')}>
          <FormattedMessage id={promptText.dataProtectionAndProcessingHeading} />
        </li>
      </NavPills>
      <div className="panel panel-default panel-body bg-lighter panel-blank position-relative padding-0">
        <div>{renderTabContent()}</div>
      </div>
      <div className="panel panel-default panel-body bg-lighter panel-blank position-relative padding-0">
        <PageNavigation
          onNext={handleGoToChat}
          onPrevious={handlePreviousSection}
          previousLabel="getStartedPage.howVirtualAssistantWorks.label"
          nextLabel="getStartedPage.common.startChat.label"
        />
      </div>
    </div>
  );
};

export default FrequentlyAskedQuestions;
